import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth, loginWithDiscord } from "@/hooks/useAuth";
import { StarsBackground } from "@/components/StarsBackground";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Lock, Sparkles, LogIn, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Values() {
  const { me, loading: authLoading } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!me) return;
    axios.get(`${API}/me/values`).then((r) => setPackages(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [me]);

  if (authLoading) {
    return (
      <div className="relative min-h-screen flex items-center justify-center">
        <StarsBackground />
        <div className="text-white/40 text-sm">Loading…</div>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="relative min-h-screen" data-testid="values-page">
        <StarsBackground />
        <Header />
        <main className="relative z-10 pt-44 pb-32 max-w-3xl mx-auto px-6">
          <div className="rv-card rounded-xl p-10 text-center">
            <Lock className="mx-auto" size={42} style={{ color: "var(--rv-red)" }} />
            <h1 className="font-display font-black uppercase mt-5 text-4xl md:text-5xl">Members only</h1>
            <p className="text-white/65 mt-4 text-base md:text-lg">Sign in with Discord to access your values.</p>
            <button onClick={loginWithDiscord} className="btn-primary mt-7" data-testid="values-login-btn">
              <LogIn size={16} /> Sign in with Discord
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Backend already filters out the VIP card (VIP unlocks everything)
  const cards = packages;
  const anyDeviceMismatch = packages.some((p) => p.device_mismatch);

  return (
    <div className="relative min-h-screen" data-testid="values-page">
      <StarsBackground />
      <Header />
      <main className="relative z-10 pt-32 md:pt-44 pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="section-label" data-testid="values-label">My Values</div>
          <h1 className="font-display font-black uppercase mt-3 text-4xl md:text-5xl lg:text-6xl leading-tight">
            Welcome back, <span style={{ color: "var(--rv-red)" }}>{me.global_name || me.username}</span>.
          </h1>
          <p className="text-white/65 mt-4 text-base md:text-lg max-w-3xl">
            Unlocked values are assigned to you using various methods to prevent leaks. If you encounter an authentication issue, contact Rumple on Discord.
          </p>

          {anyDeviceMismatch && (
            <div className="mt-8 rv-card rounded-xl p-5 flex items-start gap-3" style={{ borderColor: "rgba(212, 175, 55, 0.45)" }} data-testid="device-mismatch-banner">
              <AlertTriangle className="shrink-0 mt-0.5" size={20} style={{ color: "var(--rv-gold)" }} />
              <div className="text-sm text-white/80">
                You&apos;ve hit the device limit on this account. DM Rumple on Discord to reset the lock for your new device.
              </div>
            </div>
          )}

          {loading ? (
            <div className="mt-12 text-white/40 text-sm">Loading values…</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
              {cards.map((p) => (
                <ValueCard key={p.id} p={p} />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function ValueCard({ p }) {
  const isLocked = p.locked;
  const cardBody = (
    <div className={`rv-card rounded-xl p-6 md:p-7 h-full relative overflow-hidden ${isLocked ? "values-card-locked" : ""}`} data-testid={`value-card-${p.id}`}>
      {isLocked && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/55 backdrop-blur-[2px]" data-testid={`value-locked-${p.id}`}>
          <div className="text-center px-4">
            <Lock size={28} className="mx-auto opacity-80" />
            <div className="mt-2 text-xs uppercase tracking-[0.3em] text-white/80">
              {p.device_mismatch ? "Locked — new device" : "Not unlocked"}
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold uppercase text-xl tracking-tight">{p.name}</h3>
        <Sparkles size={16} style={{ color: "var(--rv-red)" }} />
      </div>
      <p className="text-white/60 text-sm mt-1">{p.tagline}</p>

      <ul className="mt-5 space-y-2 text-sm text-white/80">
        {(p.features || []).slice(0, 4).map((f) => (
          <li key={f} className="flex gap-2 items-start">
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 bg-[var(--rv-red)]" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {!isLocked && (
        <div className="mt-6 text-xs uppercase tracking-widest text-[var(--rv-red)]">
          Open pack →
        </div>
      )}
      {p.via_admin && !isLocked && (
        <div className="mt-2 text-[10px] uppercase tracking-[0.25em] text-[var(--rv-red)]/80">Admin access</div>
      )}
      {p.via_vip && !isLocked && !p.via_admin && (
        <div className="mt-2 text-[10px] uppercase tracking-[0.25em] text-yellow-400/80">Unlocked via VIP</div>
      )}
    </div>
  );

  if (isLocked) return cardBody;
  return (
    <Link to={`/values/${p.id}`} className="block" data-testid={`value-open-${p.id}`}>
      {cardBody}
    </Link>
  );
}
